import './ContactDetail.scss';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Envelope,
  Phone,
  LinkedinLogo,
  TwitterLogo,
  GithubLogo,
  Sparkle,
  PencilSimple,
  Trash,
  ArrowRight,
  BellRinging,
  CalendarCheck,
  Warning,
  HandWaving,
  Plus,
  RedditLogo,
  ChatsCircle,
  X as PhosphorX,
  Link,
  Check,
} from '@phosphor-icons/react';
import {
  siGmail,
  siGooglecalendar,
  siGoogle,
  siSlack,
  siWhatsapp,
  siGithub,
  siX,
  siImessage,
  siReddit,
} from 'simple-icons';
import { useContactsAppStore } from '../../store/contactsAppStore';
import {
  ContactEntity,
  ContactInteraction,
  ContactSignal,
  ContactChannel,
  TIER_LABELS,
  TIER_DESCRIPTIONS,
  TIER_CADENCE_DAYS,
} from '../../types/entity';
import { formatRelativeTime } from '../../utils/time';

interface ContactDetailProps {
  windowId: string;
  spaceId: string;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function SignalIcon({ type }: { type: ContactSignal['type'] }) {
  switch (type) {
    case 'job_change':       return <BellRinging size={14} />;
    case 'calendar_meeting': return <CalendarCheck size={14} />;
    case 'email_activity':   return <Envelope size={14} />;
    case 'linkedin_post':    return <LinkedinLogo size={14} />;
    case 'reddit_dm':        return <RedditLogo size={14} />;
    case 'slack_dm':         return <ChatsCircle size={14} />;
    case 'twitter_dm':
    case 'twitter_mention':  return <TwitterLogo size={14} />;
    case 'github_activity':  return <GithubLogo size={14} />;
    default:                 return <Warning size={14} />;
  }
}

function getInitials(name: string): string {
  return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
}

function getAvatarColorIndex(name: string): number {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return hash % 6;
}

function isStale(contact: ContactEntity): boolean {
  const cadence = TIER_CADENCE_DAYS[contact.skeleton.tier];
  if (!cadence || !contact.skeleton.lastInteraction?.at) return false;
  return (Date.now() - new Date(contact.skeleton.lastInteraction.at).getTime()) / 86400000 >= cadence;
}

// ── Source / channel badges ────────────────────────────────────────────────

function SiIcon({ icon, size = 11 }: { icon: { path: string }; size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="#fff" aria-hidden>
      <path d={icon.path} />
    </svg>
  );
}

interface SourceMeta {
  label: string;
  gradient: string;
  icon: React.ReactNode;
}

const SOURCE_META: Record<string, SourceMeta> = {
  gmail: {
    label: 'Gmail',
    gradient: 'linear-gradient(160deg, #ff6d57 0%, #EA4335 100%)',
    icon: <SiIcon icon={siGmail} />,
  },
  calendar: {
    label: 'Calendar',
    gradient: 'linear-gradient(160deg, #5fa8fb 0%, #1a73e8 100%)',
    icon: <SiIcon icon={siGooglecalendar} />,
  },
  google_contacts: {
    label: 'Contacts',
    gradient: 'linear-gradient(160deg, #5bc87a 0%, #34A853 100%)',
    icon: <SiIcon icon={siGoogle} />,
  },
  linkedin: {
    label: 'LinkedIn',
    gradient: 'linear-gradient(160deg, #2d87e0 0%, #0A66C2 100%)',
    icon: <LinkedinLogo size={11} color="#fff" weight="fill" />,
  },
  slack: {
    label: 'Slack',
    gradient: 'linear-gradient(160deg, #ed3278 0%, #611f69 100%)',
    icon: <SiIcon icon={siSlack} />,
  },
  whatsapp: {
    label: 'WhatsApp',
    gradient: 'linear-gradient(160deg, #40e07a 0%, #128C7E 100%)',
    icon: <SiIcon icon={siWhatsapp} />,
  },
  github: {
    label: 'GitHub',
    gradient: 'linear-gradient(160deg, #5a5a5a 0%, #24292E 100%)',
    icon: <SiIcon icon={siGithub} />,
  },
  twitter: {
    label: 'X / Twitter',
    gradient: 'linear-gradient(160deg, #333 0%, #000 100%)',
    icon: <SiIcon icon={siX} />,
  },
  imessage: {
    label: 'iMessage',
    gradient: 'linear-gradient(160deg, #60e87a 0%, #34C759 100%)',
    icon: <SiIcon icon={siImessage} />,
  },
  reddit: {
    label: 'Reddit',
    gradient: 'linear-gradient(160deg, #ff7043 0%, #FF4500 100%)',
    icon: <SiIcon icon={siReddit} />,
  },
  manual: {
    label: 'Manual',
    gradient: 'linear-gradient(160deg, #a0a0a0 0%, #666 100%)',
    icon: <HandWaving size={11} color="#fff" weight="fill" />,
  },
};

const CHANNEL_ICONS: Record<ContactChannel, React.ReactNode> = {
  email:    <Envelope size={12} />,
  calendar: <CalendarCheck size={12} />,
  slack:    <ChatsCircle size={12} />,
  whatsapp: <Phone size={12} />,
  phone:    <Phone size={12} />,
  linkedin: <LinkedinLogo size={12} />,
  reddit:   <RedditLogo size={12} />,
  twitter:  <TwitterLogo size={12} />,
  github:   <GithubLogo size={12} />,
  other:    <Link size={12} />,
};

function SourceBadge({ source }: { source: string }) {
  const meta: SourceMeta = SOURCE_META[source] ?? {
    label: source.charAt(0).toUpperCase() + source.slice(1),
    gradient: 'linear-gradient(160deg, #a0a0a0 0%, #666 100%)',
    icon: <HandWaving size={11} color="#fff" weight="fill" />,
  };
  return (
    <div className="contact-detail__source-badge">
      <span className="contact-detail__source-icon" style={{ background: meta.gradient }}>
        {meta.icon}
      </span>
      <span className="contact-detail__source-label">{meta.label}</span>
    </div>
  );
}

// ── Channel pill in header ────────────────────────────────────────────────

function PrimaryChannelPill({ channel, context }: { channel?: string; context?: string }) {
  if (!channel) return null;
  const meta = SOURCE_META[channel];
  const label = context || meta?.label || channel;
  return (
    <div className="contact-detail__primary-channel">
      <span
        className="contact-detail__primary-channel-icon"
        style={{ background: meta?.gradient ?? 'linear-gradient(160deg,#a0a0a0,#666)' }}
      >
        {meta?.icon ?? <Link size={10} color="#fff" />}
      </span>
      <span className="contact-detail__primary-channel-label">{label}</span>
    </div>
  );
}

// ── Identity type badge ───────────────────────────────────────────────────

function IdentityBadge({ type }: { type: string }) {
  if (type === 'person' || !type) return null;
  if (type === 'ai') return <span className="contact-detail__identity-badge contact-detail__identity-badge--ai">AI</span>;
  if (type === 'account') return <span className="contact-detail__identity-badge contact-detail__identity-badge--account">Account</span>;
  return <span className="contact-detail__identity-badge">{type}</span>;
}

// ── Warmth indicator ──────────────────────────────────────────────────────

function WarmthIndicator({ score }: { score: number }) {
  const level = score > 0.7 ? 'hot' : score > 0.4 ? 'warm' : score > 0.1 ? 'cool' : 'cold';
  const label = level === 'hot' ? '🔥' : level === 'warm' ? '🟡' : level === 'cool' ? '🔵' : '⚫';
  return (
    <span
      className={`contact-detail__warmth contact-detail__warmth--${level}`}
      title={`Warmth: ${(score * 100).toFixed(0)}% (${level})`}
    >
      {label} {level}
    </span>
  );
}

// ── Interaction row ───────────────────────────────────────────────────────

function InteractionRow({
  interaction,
  onDelete,
}: {
  interaction: ContactInteraction;
  onDelete?: (id: string) => void;
}) {
  const meta = SOURCE_META[interaction.channel] ?? SOURCE_META.manual;
  return (
    <div className="contact-detail__timeline-item">
      <span
        className="contact-detail__timeline-icon"
        style={{ background: meta.gradient }}
        title={interaction.channel}
      >
        {CHANNEL_ICONS[interaction.channel as ContactChannel] ?? <Link size={10} />}
      </span>
      <div className="contact-detail__timeline-body">
        <div className="contact-detail__timeline-meta">
          <span className="contact-detail__timeline-when">{formatRelativeTime(interaction.at)}</span>
          <span className="contact-detail__timeline-channel">{interaction.channel}</span>
          <span className={`contact-detail__timeline-dir contact-detail__timeline-dir--${interaction.direction}`}>
            {interaction.direction}
          </span>
        </div>
        {interaction.snippet && (
          <p className="contact-detail__timeline-snippet">{interaction.snippet}</p>
        )}
      </div>
      {onDelete && (
        <button
          className="contact-detail__timeline-delete"
          onClick={() => onDelete(interaction._id)}
          title="Remove"
        >
          <PhosphorX size={11} />
        </button>
      )}
    </div>
  );
}

// ── Log interaction form ──────────────────────────────────────────────────

interface LogFormData {
  channel: ContactChannel;
  direction: 'inbound' | 'outbound' | 'both';
  snippet: string;
}

function InteractionLogForm({ onSubmit, onCancel }: {
  onSubmit: (data: LogFormData) => void;
  onCancel: () => void;
}) {
  const [channel, setChannel] = useState<ContactChannel>('email');
  const [direction, setDirection] = useState<'inbound' | 'outbound' | 'both'>('outbound');
  const [snippet, setSnippet] = useState('');

  const channels: ContactChannel[] = ['email', 'slack', 'reddit', 'twitter', 'phone', 'calendar', 'linkedin', 'whatsapp', 'github', 'other'];

  return (
    <div className="contact-detail__log-form">
      <div className="contact-detail__log-form-row">
        <select
          className="contact-detail__log-select"
          value={channel}
          onChange={(e) => setChannel(e.target.value as ContactChannel)}
        >
          {channels.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select
          className="contact-detail__log-select"
          value={direction}
          onChange={(e) => setDirection(e.target.value as 'inbound' | 'outbound' | 'both')}
        >
          <option value="outbound">outbound</option>
          <option value="inbound">inbound</option>
          <option value="both">both</option>
        </select>
      </div>
      <textarea
        className="contact-detail__log-textarea"
        placeholder="Optional note (e.g. 'Discussed Q1 roadmap')"
        value={snippet}
        onChange={(e) => setSnippet(e.target.value)}
        rows={2}
      />
      <div className="contact-detail__log-form-actions">
        <button
          className="contact-detail__log-btn contact-detail__log-btn--primary"
          onClick={() => onSubmit({ channel, direction, snippet })}
        >
          Log
        </button>
        <button className="contact-detail__log-btn" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export const ContactDetail: React.FC<ContactDetailProps> = ({ windowId, spaceId }) => {
  const store = useContactsAppStore(windowId, spaceId);
  const selectedContactId  = store((s) => s.selectedContactId);
  const contacts           = store((s) => s.contacts);
  const enrichingContactIds = store((s) => s.enrichingContactIds);
  const enrichContact      = store((s) => s.enrichContact);
  const deleteContact      = store((s) => s.deleteContact);
  const openForm           = store((s) => s.openForm);
  const selectContact      = store((s) => s.selectContact);
  const interactions       = store((s) => s.interactions);
  const interactionsLoading = store((s) => s.interactionsLoading);
  const logInteraction     = store((s) => s.logInteraction);
  const deleteInteraction  = store((s) => s.deleteInteraction);
  const fetchInteractions  = store((s) => s.fetchInteractions);

  const contact = contacts.find((c) => c._id === selectedContactId);
  const contactInteractions = selectedContactId ? (interactions[selectedContactId] ?? []) : [];
  const isLoadingInteractions = selectedContactId ? interactionsLoading.has(selectedContactId) : false;

  const [showLogForm, setShowLogForm] = useState(false);
  const [showAllInteractions, setShowAllInteractions] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  useEffect(() => {
    if (selectedContactId && !interactions[selectedContactId] && !interactionsLoading.has(selectedContactId)) {
      fetchInteractions(selectedContactId);
    }
  }, [selectedContactId]);

  const handleEnrich = useCallback(() => {
    if (contact) enrichContact(contact._id);
  }, [contact, enrichContact]);

  const handleEdit = useCallback(() => {
    if (contact) openForm(contact._id);
  }, [contact, openForm]);

  const handleDeleteClick = useCallback(() => {
    setConfirmingDelete(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!contact) return;
    setConfirmingDelete(false);
    await deleteContact(contact._id);
    selectContact(null);
  }, [contact, deleteContact, selectContact]);

  const handleDeleteCancel = useCallback(() => {
    setConfirmingDelete(false);
  }, []);

  const handleLogInteraction = useCallback(async (data: LogFormData) => {
    if (!contact) return;
    setShowLogForm(false);
    await logInteraction(contact._id, data);
  }, [contact, logInteraction]);

  const handleDeleteInteraction = useCallback(async (interactionId: string) => {
    if (!contact) return;
    await deleteInteraction(contact._id, interactionId);
  }, [contact, deleteInteraction]);

  if (!contact) return null;

  const sk = contact.skeleton;
  const isEnriching = enrichingContactIds.has(contact._id);
  const stale = isStale(contact);
  const displayInteractions = showAllInteractions ? contactInteractions : contactInteractions.slice(0, 5);

  const primaryChannel = sk.primaryChannel ?? (
    sk.handles?.slack   ? 'slack'   :
    sk.handles?.reddit  ? 'reddit'  :
    sk.handles?.twitter ? 'twitter' : undefined
  );

  const relatedContacts = sk.relatedContactIds
    ?.map((id) => contacts.find((c) => c._id === id))
    .filter(Boolean) as ContactEntity[];

  return (
    <div className="contact-detail">
      <div className="contact-detail__constrained">

        {/* Header */}
        <div className="contact-detail__header">
          <div
            className="contact-detail__avatar"
            data-color={sk.avatarUrl ? undefined : getAvatarColorIndex(contact.name)}
          >
            {sk.avatarUrl ? (
              <img src={sk.avatarUrl} alt={contact.name} className="contact-detail__avatar-img" />
            ) : (
              <span className="contact-detail__avatar-initials">{getInitials(contact.name)}</span>
            )}
            {stale && (
              <span className="contact-detail__stale-badge" title="Overdue for reach-out">!</span>
            )}
          </div>
          <div className="contact-detail__header-info">
            <div className="contact-detail__name-row">
              <h2 className="contact-detail__name">{contact.name}</h2>
              {sk.identityType && sk.identityType !== 'person' && (
                <IdentityBadge type={sk.identityType} />
              )}
            </div>
            {(sk.title || sk.company) && (
              <p className="contact-detail__role">
                {sk.title}{sk.title && sk.company ? ' · ' : ''}{sk.company}
              </p>
            )}
            <div className="contact-detail__header-badges">
              <span className={`contact-detail__tier-badge contact-detail__tier-badge--${sk.tier}`}>
                {TIER_LABELS[sk.tier]}
                <span className="contact-detail__tier-desc"> — {TIER_DESCRIPTIONS[sk.tier]}</span>
              </span>
              {sk.warmthScore !== undefined && sk.warmthScore > 0 && (
                <WarmthIndicator score={sk.warmthScore} />
              )}
            </div>
            {primaryChannel && (
              <PrimaryChannelPill
                channel={primaryChannel}
                context={sk.primaryChannelContext}
              />
            )}
          </div>
          <div className="contact-detail__header-actions">
            {confirmingDelete ? (
              <>
                <span className="contact-detail__delete-confirm-label">Delete?</span>
                <button
                  className="contact-detail__icon-btn contact-detail__icon-btn--danger-solid"
                  onClick={handleDeleteConfirm}
                  title="Confirm delete"
                >
                  <Check size={15} />
                </button>
                <button
                  className="contact-detail__icon-btn"
                  onClick={handleDeleteCancel}
                  title="Cancel"
                >
                  <PhosphorX size={15} />
                </button>
              </>
            ) : (
              <>
                <button className="contact-detail__icon-btn" onClick={handleEdit} title="Edit">
                  <PencilSimple size={15} />
                </button>
                <button
                  className={`contact-detail__icon-btn contact-detail__icon-btn--enrich${isEnriching ? ' contact-detail__icon-btn--loading' : ''}`}
                  onClick={handleEnrich}
                  disabled={isEnriching}
                  title="Enrich from integrations"
                >
                  <Sparkle size={15} />
                </button>
                <button
                  className="contact-detail__icon-btn contact-detail__icon-btn--danger"
                  onClick={handleDeleteClick}
                  title="Delete"
                >
                  <Trash size={15} />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Stale warning */}
        {stale && (
          <div className="contact-detail__stale-warning">
            <Warning size={13} weight="fill" />
            Overdue for reach-out
          </div>
        )}

        {/* ── Contact info + socials ── */}
        <div className="contact-detail__section contact-detail__section--first">
          <h3 className="contact-detail__section-title">Contact</h3>
          <div className="contact-detail__info-rows">

            {sk.email ? (
              <a href={`mailto:${sk.email}`} className="contact-detail__info-row">
                <Envelope size={14} />
                <span>{sk.email}</span>
                {sk.preferredChannel === 'email' && <span className="contact-detail__preferred-badge">preferred</span>}
              </a>
            ) : (
              <button className="contact-detail__info-row contact-detail__info-row--empty" onClick={handleEdit}>
                <Envelope size={14} />
                <span>Add email</span>
              </button>
            )}

            {sk.phone ? (
              <a href={`tel:${sk.phone}`} className="contact-detail__info-row">
                <Phone size={14} />
                <span>{sk.phone}</span>
                {sk.preferredChannel === 'phone' && <span className="contact-detail__preferred-badge">preferred</span>}
              </a>
            ) : (
              <button className="contact-detail__info-row contact-detail__info-row--empty" onClick={handleEdit}>
                <Phone size={14} />
                <span>Add phone</span>
              </button>
            )}

            {sk.linkedinUrl ? (
              <a href={sk.linkedinUrl} target="_blank" rel="noreferrer" className="contact-detail__info-row">
                <LinkedinLogo size={14} />
                <span>LinkedIn</span>
              </a>
            ) : (
              <button className="contact-detail__info-row contact-detail__info-row--empty" onClick={handleEdit}>
                <LinkedinLogo size={14} />
                <span>Add LinkedIn</span>
              </button>
            )}

            {(sk.handles?.twitter || sk.twitterHandle) ? (
              <a
                href={`https://twitter.com/${sk.handles?.twitter || sk.twitterHandle}`}
                target="_blank"
                rel="noreferrer"
                className="contact-detail__info-row"
              >
                <TwitterLogo size={14} />
                <span>@{sk.handles?.twitter || sk.twitterHandle}</span>
              </a>
            ) : (
              <button className="contact-detail__info-row contact-detail__info-row--empty" onClick={handleEdit}>
                <TwitterLogo size={14} />
                <span>Add X / Twitter</span>
              </button>
            )}

            {(sk.handles?.github || sk.githubHandle) ? (
              <a
                href={`https://github.com/${sk.handles?.github || sk.githubHandle}`}
                target="_blank"
                rel="noreferrer"
                className="contact-detail__info-row"
              >
                <GithubLogo size={14} />
                <span>{sk.handles?.github || sk.githubHandle}</span>
              </a>
            ) : (
              <button className="contact-detail__info-row contact-detail__info-row--empty" onClick={handleEdit}>
                <GithubLogo size={14} />
                <span>Add GitHub</span>
              </button>
            )}

            {sk.handles?.reddit && (
              <a
                href={`https://reddit.com/user/${sk.handles.reddit.replace(/^u\//, '')}`}
                target="_blank"
                rel="noreferrer"
                className="contact-detail__info-row"
              >
                <RedditLogo size={14} />
                <span>{sk.handles.reddit}</span>
              </a>
            )}

            {sk.handles?.slack && (
              <div className="contact-detail__info-row">
                <ChatsCircle size={14} />
                <span>
                  {sk.handles.slackWorkspace
                    ? `${sk.handles.slack} · ${sk.handles.slackWorkspace}`
                    : sk.handles.slack}
                </span>
              </div>
            )}

          </div>
        </div>

        {/* ── Interaction timeline ── */}
        <div className="contact-detail__section">
          <div className="contact-detail__section-header">
            <h3 className="contact-detail__section-title">Recent Interactions</h3>
            <button
              className="contact-detail__add-btn"
              onClick={() => setShowLogForm((v) => !v)}
              title="Log interaction"
            >
              <Plus size={12} weight="bold" />
            </button>
          </div>

          {showLogForm && (
            <InteractionLogForm
              onSubmit={handleLogInteraction}
              onCancel={() => setShowLogForm(false)}
            />
          )}

          {isLoadingInteractions ? (
            <p className="contact-detail__timeline-empty">Loading…</p>
          ) : contactInteractions.length === 0 ? (
            <p className="contact-detail__timeline-empty">
              No interactions recorded yet. Click + to log one.
            </p>
          ) : (
            <div className="contact-detail__timeline">
              {displayInteractions.map((i) => (
                <InteractionRow
                  key={i._id}
                  interaction={i}
                  onDelete={handleDeleteInteraction}
                />
              ))}
              {contactInteractions.length > 5 && (
                <button
                  className="contact-detail__timeline-more"
                  onClick={() => setShowAllInteractions((v) => !v)}
                >
                  {showAllInteractions
                    ? 'Show less'
                    : `Show ${contactInteractions.length - 5} more`}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Next reach-out */}
        {sk.nextReachOutAt && (
          <div className="contact-detail__section">
            <h3 className="contact-detail__section-title">Suggested Reach-Out</h3>
            <div className="contact-detail__reachout">
              <ArrowRight size={13} />
              <span>{formatDate(sk.nextReachOutAt)}</span>
              {sk.nextReachOutReason && (
                <span className="contact-detail__reachout-reason"> — {sk.nextReachOutReason}</span>
              )}
            </div>
          </div>
        )}

        {/* Topics */}
        {sk.topics && sk.topics.length > 0 && (
          <div className="contact-detail__section">
            <h3 className="contact-detail__section-title">Topics We've Discussed</h3>
            <div className="contact-detail__topics">
              {sk.topics.map((t) => (
                <span key={t} className="contact-detail__topic-chip">{t}</span>
              ))}
            </div>
          </div>
        )}

        {/* Recent signals */}
        {sk.signals && sk.signals.length > 0 && (
          <div className="contact-detail__section">
            <h3 className="contact-detail__section-title">Recent Signals</h3>
            <div className="contact-detail__signals">
              {sk.signals.slice(0, 5).map((signal) => (
                <div key={signal.id} className="contact-detail__signal">
                  <span className="contact-detail__signal-icon">
                    <SignalIcon type={signal.type} />
                  </span>
                  <div className="contact-detail__signal-content">
                    <span className="contact-detail__signal-title">{signal.title}</span>
                    {signal.body && <p className="contact-detail__signal-body">{signal.body}</p>}
                    <span className="contact-detail__signal-date">
                      {formatRelativeTime(signal.detectedAt)} · {signal.source}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tags */}
        {sk.tags && sk.tags.length > 0 && (
          <div className="contact-detail__section">
            <h3 className="contact-detail__section-title">Tags</h3>
            <div className="contact-detail__topics">
              {sk.tags.map((t) => (
                <span key={t} className="contact-detail__tag-chip">{t}</span>
              ))}
            </div>
          </div>
        )}

        {/* Notes */}
        {sk.notes && (
          <div className="contact-detail__section">
            <h3 className="contact-detail__section-title">Notes</h3>
            <p className="contact-detail__notes">{sk.notes}</p>
          </div>
        )}

        {/* Related contacts */}
        {relatedContacts && relatedContacts.length > 0 && (
          <div className="contact-detail__section">
            <h3 className="contact-detail__section-title">Related Accounts</h3>
            <div className="contact-detail__related">
              {relatedContacts.map((rc) => (
                <button
                  key={rc._id}
                  className="contact-detail__related-item"
                  onClick={() => selectContact(rc._id)}
                >
                  <div className="contact-detail__related-avatar">
                    {rc.skeleton.avatarUrl ? (
                      <img src={rc.skeleton.avatarUrl} alt={rc.name} />
                    ) : (
                      <span>{getInitials(rc.name)}</span>
                    )}
                  </div>
                  <div className="contact-detail__related-info">
                    <span className="contact-detail__related-name">{rc.name}</span>
                    {rc.skeleton.identityType && (
                      <span className="contact-detail__related-type">{rc.skeleton.identityType}</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Sources */}
        {sk.sources && sk.sources.length > 0 && (
          <div className="contact-detail__sources">
            <div className="contact-detail__sources-row">
              {sk.sources.map((s) => <SourceBadge key={s} source={s} />)}
            </div>
            {sk.lastEnrichedAt && (
              <span className="contact-detail__sources-enriched">
                Last enriched {formatRelativeTime(sk.lastEnrichedAt)}
              </span>
            )}
          </div>
        )}

      </div>
    </div>
  );
};
